/**
 * Shared contract for the deal real-time change channel (Server-Sent Events).
 *
 * Endpoint: `GET /deals/:dealId/events` — `Content-Type: text/event-stream`.
 *
 * The server pushes lightweight change *signals*, never deal/items state. On each signal the
 * client re-reads canonical state over HTTP:
 *   - `SYNC` / `DEAL`  → refetch the deal (the `deal` GraphQL query)
 *   - `SYNC` / `ITEMS` → reload the items (`GET /deals/:dealId/items/stream`, or `POST` with a
 *     {@link DealItemsStreamBody} to narrow that read)
 *
 * Wire format — each SSE message is a single `data:` line whose JSON body is a
 * {@link DealChangeNotification}, e.g.:
 *
 *     data: {"kind":"ITEMS","action":"itemsUpdated","itemIds":["di_01H…"]}\n\n
 *
 * Lines starting with `:` are keep-alive comments and must be ignored by the client.
 *
 * This file is the single source of truth for the contract and is meant to be shared verbatim
 * between the deals backend and the frontend.
 */

/**
 * JSON body of the filtered items reload, `POST /deals/:dealId/items/stream`.
 *
 * `GET /deals/:dealId/items/stream` streams the deal's whole active version. The `POST` form
 * narrows the read to the items listed in `itemIds`, which is what a client that knows exactly
 * what changed should send rather than re-reading megabytes to refresh a handful of rows:
 *
 *     POST /deals/:dealId/items/stream
 *     Content-Type: application/json
 *
 *     { "itemIds": ["di_a", "di_b"] }
 *
 * The ids ride the body rather than the querystring because a few hundred of them already exceed
 * what an HTTP request head may carry (Node refuses past 16 KiB, Bearer token included), and the
 * read is neither cacheable nor bookmarkable: it follows a change signal and must see fresh state.
 *
 * The response shape does not change: the same batches, ending on the same `isLastBatch`, so a
 * client can run the filtered read through the reader it already has.
 *
 * The server errs towards answering with more than was asked for. A list it cannot honour exactly
 * — missing, malformed, blank, or longer than it will filter on — streams the whole version
 * instead of a truncated answer, since a client would read missing items as unchanged and keep
 * showing stale rows. A filtered read is therefore always safe to attempt; it is never the reason
 * data goes missing.
 */
export type DealItemsStreamBody = {
    itemIds?: string[]
}

/** Signal kinds pushed over the channel. */
export const DEAL_CHANGE_KINDS = ["SYNC", "DEAL", "ITEMS"] as const

export type DealChangeKind = (typeof DEAL_CHANGE_KINDS)[number]

/** The action that produced an `ITEMS` change. */
export const DEAL_ITEMS_ACTION_TYPES = [
    "datasourceProcessed",
    "itemsCreated",
    "itemsUpdated",
    "itemsDeleted",
] as const

export type DealItemsActionType = (typeof DEAL_ITEMS_ACTION_TYPES)[number]

/**
 * A single change signal (the JSON carried by an SSE `data:` line), discriminated on `kind`:
 *   - `SYNC`  — emitted on every (re)connect once the server is subscribed. Reload everything.
 *   - `DEAL`  — the deal itself changed. Refetch the deal.
 *   - `ITEMS` — deal items changed (see `action`). Reload the items in `itemIds`, drop the ones
 *     in `deletedItemIds`.
 *
 * `itemIds` names the items that changed, so the reload can be narrowed to them through
 * {@link DealItemsStreamBody} instead of replaying the whole version.
 *
 * `deletedItemIds` names the items that left the version. They are not to be reloaded — a
 * narrowed read never returns a deleted item — but removed from the client's state on the spot.
 * The two lists are kept apart because a burst coalesces into one signal carrying only the
 * latest `action`: "A updated" then "C deleted" would otherwise read as both deleted. `action`
 * is informational; the lists are what the client acts on.
 *
 * An **empty `itemIds` with an empty `deletedItemIds` means the server is not naming them**, and
 * the client must reload everything — which is what it did before these fields existed. That is
 * the answer whenever naming them would cost more than it saves: a bulk change (import, mass
 * edit, mass deletion) past what the reload endpoint will filter on. When `deletedItemIds` is not
 * empty, `itemIds` is the exact list of items to reload, possibly empty — a deletion on its own
 * needs no read at all.
 *
 * A client that predates `deletedItemIds` ignores it and, seeing an empty `itemIds`, reloads
 * everything on a deletion as it always did: the field is safe to ship to any client.
 *
 * Empty never means "nothing changed": a signal is only ever emitted for a change. Anything that
 * widens the blast radius collapses both lists to empty rather than to a partial one, so a client
 * that trusts them can never miss a change.
 */
export type DealChangeNotification =
    | { kind: "SYNC" }
    | { kind: "DEAL" }
    | {
          kind: "ITEMS"
          action: DealItemsActionType
          itemIds: string[]
          deletedItemIds: string[]
      }
